// Route → breadcrumb trail of the PageHeader. Pure helpers: no React / HTTP here.
// Section labels reuse the side menu keys (SideMenuItems.tsx) so the trail reads like the menu.
import { matchPath } from 'react-router-dom';
import { AppState } from '../../../redux/reducers';
import { selectStorageOwnerNames } from '../../../components/Files/FilesExplorer/FilesExplorer.Functions';
import {
  StorageEntityKind,
  StorageEntityRef,
  StorageOwnerNames,
  Translate,
  formatStorageEntityLabel,
  getEntityNameKey,
  isResolvableEntityRef,
} from '../../../functions/StorageBrowser.Functions';

/** One level of the trail. Without `href` it is a group label (Finanzas) or the current page. */
export interface BreadcrumbItemDef {
  key: string;
  label: string;
  href?: string;
}

/** Entities shown by id in detail routes. The storage kinds get their name resolved by id. */
export type PageEntityKind = StorageEntityKind | 'contract' | 'medicalInsurance' | 'agreement' | 'staff' | 'tenantPriceList';

export interface PageEntityRef {
  kind: PageEntityKind;
  id: string;
}

const STORAGE_KINDS: StorageEntityKind[] = ['provider', 'sponsor', 'protocol', 'settlement', 'contract', 'practitioner'];

const isStorageKind = (kind: PageEntityKind): kind is StorageEntityKind =>
  (STORAGE_KINDS as string[]).includes(kind);

const PAGE_ENTITY_NAME_KEY_PREFIX: Record<Exclude<PageEntityKind, StorageEntityKind>, string> = {
  medicalInsurance: 'obrasocial_',
  agreement: 'convenio_',
  staff: 'staff_',
  tenantPriceList: 'listaprecios_',
};

const PAGE_ENTITY_LABEL_KEYS: Record<Exclude<PageEntityKind, StorageEntityKind>, string> = {
  medicalInsurance: 'filesExplorer.folderNames.medicalInsurance',
  agreement: 'pageHeader.breadcrumbs.entities.agreement',
  staff: 'pageHeader.breadcrumbs.entities.staff',
  tenantPriceList: 'pageHeader.breadcrumbs.entities.tenantPriceList',
};

/** Key of an entity inside the names map (same keys as the storage explorer for its kinds). */
export const getPageEntityNameKey = (kind: PageEntityKind, id: string | number): string =>
  isStorageKind(kind) ? getEntityNameKey(kind, id) : `${PAGE_ENTITY_NAME_KEY_PREFIX[kind]}${id}`;

/** "Protocolo 31 - PRESTO" / "Contrato 12 - Anual" when the name is known, "Contrato 12" otherwise. */
export const formatPageEntityLabel = (ref: PageEntityRef, t: Translate, names: StorageOwnerNames = {}): string => {
  if (isStorageKind(ref.kind)) return formatStorageEntityLabel({ kind: ref.kind, id: ref.id }, t, names);
  const label = t(PAGE_ENTITY_LABEL_KEYS[ref.kind], { id: ref.id });
  const name = names[getPageEntityNameKey(ref.kind, ref.id)];
  return name ? t('filesExplorer.folderNames.named', { label, name }) : label;
};

//#region route map

type Params = Record<string, string | undefined>;

interface CrumbDef {
  key: string;
  href?: string;
  /** Section label (i18n key) ... */
  labelKey?: string;
  /** ... or an entity shown by id. */
  entity?: PageEntityRef;
}

const section = (key: string, labelKey: string, href?: string): CrumbDef => ({ key, labelKey, href });
const entity = (kind: PageEntityKind, id: string | undefined, href: string): CrumbDef => ({
  key: `${kind}-${id}`,
  entity: { kind, id: id ?? '' },
  href,
});

const HOME = section('home', 'sideMenu.home', '/');
const INSURANCES = section('insurances', 'sideMenu.healthInsurance', '/insurances');
const FINANCE = section('finance', 'pageHeader.breadcrumbs.finance');

const insurance = (p: Params) => entity('medicalInsurance', p.MedicalInsuranceId, `/insurances/${p.MedicalInsuranceId}`);
const agreements = (p: Params) =>
  section('agreements', 'pageHeader.breadcrumbs.agreements', `/insurances/${p.MedicalInsuranceId}/agreements`);

/** Every application route (App.tsx) with its trail after "Inicio". Order matters: first match wins. */
export const PAGE_BREADCRUMB_ROUTES: { pattern: string; crumbs: (params: Params) => CrumbDef[] }[] = [
  { pattern: '/', crumbs: () => [] },
  { pattern: '/medicalservices', crumbs: () => [section('medicalservices', 'sideMenu.healthServices', '/medicalservices')] },
  { pattern: '/services', crumbs: () => [section('services', 'sideMenu.services', '/services')] },
  { pattern: '/offices', crumbs: () => [section('offices', 'sideMenu.consultorios', '/offices')] },
  { pattern: '/insurances', crumbs: () => [INSURANCES] },
  { pattern: '/insurances/:MedicalInsuranceId', crumbs: (p) => [INSURANCES, insurance(p)] },
  { pattern: '/insurances/:MedicalInsuranceId/agreements', crumbs: (p) => [INSURANCES, insurance(p), agreements(p)] },
  {
    pattern: '/insurances/:MedicalInsuranceId/agreements/:AgreementId',
    crumbs: (p) => [
      INSURANCES,
      insurance(p),
      agreements(p),
      entity('agreement', p.AgreementId, `/insurances/${p.MedicalInsuranceId}/agreements/${p.AgreementId}`),
    ],
  },
  {
    pattern: '/tenantpricelist/:TenantPriceListId',
    crumbs: (p) => [INSURANCES, entity('tenantPriceList', p.TenantPriceListId, `/tenantpricelist/${p.TenantPriceListId}`)],
  },
  { pattern: '/protocolsagreements', crumbs: () => [section('protocols', 'sideMenu.protocols', '/protocolsagreements')] },
  {
    pattern: '/protocolsagreements/:ProtocolId',
    crumbs: (p) => [
      section('protocols', 'sideMenu.protocols', '/protocolsagreements'),
      entity('protocol', p.ProtocolId, `/protocolsagreements/${p.ProtocolId}`),
    ],
  },
  { pattern: '/providers', crumbs: () => [section('providers', 'sideMenu.providers', '/providers')] },
  {
    pattern: '/providers/:ProviderId',
    crumbs: (p) => [
      section('providers', 'sideMenu.providers', '/providers'),
      entity('provider', p.ProviderId, `/providers/${p.ProviderId}`),
    ],
  },
  { pattern: '/sponsors', crumbs: () => [section('sponsors', 'sideMenu.sponsors', '/sponsors')] },
  {
    pattern: '/sponsors/:SponsorId',
    crumbs: (p) => [
      section('sponsors', 'sideMenu.sponsors', '/sponsors'),
      entity('sponsor', p.SponsorId, `/sponsors/${p.SponsorId}`),
    ],
  },
  { pattern: '/staff', crumbs: () => [section('staff', 'sideMenu.staff', '/staff')] },
  {
    pattern: '/staff/:PersonId',
    crumbs: (p) => [section('staff', 'sideMenu.staff', '/staff'), entity('staff', p.PersonId, `/staff/${p.PersonId}`)],
  },
  { pattern: '/practitioners', crumbs: () => [section('practitioners', 'sideMenu.practitioners', '/practitioners')] },
  {
    pattern: '/practitioners/:PractitionerId',
    crumbs: (p) => [
      section('practitioners', 'sideMenu.practitioners', '/practitioners'),
      entity('practitioner', p.PractitionerId, `/practitioners/${p.PractitionerId}`),
    ],
  },
  { pattern: '/contracts', crumbs: () => [section('contracts', 'sideMenu.contracts', '/contracts')] },
  {
    pattern: '/contracts/:ContractId',
    crumbs: (p) => [
      section('contracts', 'sideMenu.contracts', '/contracts'),
      entity('contract', p.ContractId, `/contracts/${p.ContractId}`),
    ],
  },
  {
    // Revisión con IA de un contrato: "Inicio > Contratos > Contrato N > Revisión con IA".
    pattern: '/contracts/:ContractId/reviews/:ReviewId',
    crumbs: (p) => [
      section('contracts', 'sideMenu.contracts', '/contracts'),
      entity('contract', p.ContractId, `/contracts/${p.ContractId}`),
      section('contract-review', 'contractReview.page.title'),
    ],
  },
  { pattern: '/settlements', crumbs: () => [section('settlements', 'sideMenu.settlements', '/settlements')] },
  {
    pattern: '/settlements/:SettlementId',
    crumbs: (p) => [
      section('settlements', 'sideMenu.settlements', '/settlements'),
      entity('settlement', p.SettlementId, `/settlements/${p.SettlementId}`),
    ],
  },
  { pattern: '/cashflow', crumbs: () => [FINANCE, section('cashflow', 'sideMenu.cashflow', '/cashflow')] },
  { pattern: '/proyections', crumbs: () => [FINANCE, section('proyections', 'sideMenu.proyections', '/proyections')] },
  // The explorer has its own folder breadcrumb: the header only says where the page is (?path is ignored).
  { pattern: '/files', crumbs: () => [section('files', 'filesExplorer.title', '/files')] },
  { pattern: '/more-settings', crumbs: () => [section('settings', 'sideMenu.settings', '/more-settings')] },
];

const matchCrumbs = (pathname: string): CrumbDef[] | undefined => {
  for (const route of PAGE_BREADCRUMB_ROUTES) {
    const match = matchPath({ path: route.pattern, end: true }, pathname || '/');
    if (match) return [HOME, ...route.crumbs(match.params as Params)];
  }
  return undefined;
};

/**
 * Trail of a pathname: "Inicio" first, then the sections and the entity. The last item is the
 * current page (no href). Unknown routes give an empty trail (the header shows its eyebrow).
 */
export const buildPageBreadcrumbs = (
  pathname: string,
  t: Translate,
  names: StorageOwnerNames = {}
): BreadcrumbItemDef[] => {
  const crumbs = matchCrumbs(pathname);
  if (!crumbs) return [];
  return crumbs.map((crumb, index) => {
    const label = crumb.entity ? formatPageEntityLabel(crumb.entity, t, names) : t(crumb.labelKey ?? '');
    const isCurrent = index === crumbs.length - 1;
    return isCurrent || !crumb.href ? { key: crumb.key, label } : { key: crumb.key, label, href: crumb.href };
  });
};

/** Entities of the trail whose name can be looked up by id (StorageBrowser ResolveEntityNames). */
export const getResolvableEntityRefs = (pathname: string): StorageEntityRef[] =>
  (matchCrumbs(pathname) ?? [])
    .map((crumb) => crumb.entity)
    .filter((ref): ref is PageEntityRef => !!ref && isStorageKind(ref.kind))
    .map((ref) => ({ kind: ref.kind as StorageEntityKind, id: ref.id }))
    .filter(isResolvableEntityRef);

//#endregion

//#region back button

/** Closest ancestor of the current page that can be opened. */
export const getParentHref = (items: BreadcrumbItemDef[]): string | undefined => {
  for (let index = items.length - 2; index >= 0; index--) {
    if (items[index].href) return items[index].href;
  }
  return undefined;
};

/**
 * Where "Volver" goes: the explicit `backTo`; else the previous in-app history entry (-1) when
 * there is one; else the parent of the trail; else home.
 */
export const resolveBackTarget = (options: {
  backTo?: string;
  hasAppHistory: boolean;
  parentHref?: string;
}): string | -1 => {
  if (options.backTo) return options.backTo;
  if (options.hasAppHistory) return -1;
  return options.parentHref ?? '/';
};

/**
 * react-router (history v5) numbers the entries of the session in window.history.state.idx:
 * idx > 0 means the previous entry belongs to the app.
 */
export const hasAppHistory = (historyState: unknown): boolean => {
  const idx = (historyState as { idx?: unknown } | null | undefined)?.idx;
  return typeof idx === 'number' && idx > 0;
};

//#endregion

//#region names

type Named<T> = T[] | undefined;

/** Names of insurances, staff people and price lists already in the store (contracts come with the storage names). */
export const buildPageEntityNames = (
  storageNames: StorageOwnerNames,
  insurances: Named<{ id: number; businessName?: string | null; title?: string | null }>,
  staff: Named<{ person?: { id: number; businessName?: string | null } | null }>,
  persons: Named<{ id: number; businessName?: string | null }>,
  priceLists: Named<{ id: number; listName?: string | null; priceList?: { listName?: string | null } | null }>
): StorageOwnerNames => {
  const names: StorageOwnerNames = { ...storageNames };
  const add = (kind: PageEntityKind, id: number | undefined, name: string | null | undefined) => {
    const value = name?.trim();
    if (value && id !== undefined && id !== null) names[getPageEntityNameKey(kind, id)] = value;
  };
  (insurances ?? []).forEach((i) => add('medicalInsurance', i?.id, i?.businessName || i?.title));
  (persons ?? []).forEach((p) => add('staff', p?.id, p?.businessName));
  (staff ?? []).forEach((s) => add('staff', s?.person?.id, s?.person?.businessName));
  (priceLists ?? []).forEach((l) => add('tenantPriceList', l?.id, l?.listName || l?.priceList?.listName));
  return names;
};

let pageNamesCache: { sources: unknown[]; names: StorageOwnerNames } | undefined;

/** Every entity name the header can show, memoized on its store sources. */
export const selectPageEntityNames = (state: AppState): StorageOwnerNames => {
  const storageNames = selectStorageOwnerNames(state);
  const sources = [
    storageNames,
    state.MedicalInsurances?.list,
    state.Staff?.list,
    state.Persons?.list,
    state.TenantPriceLists?.list,
  ];
  if (!pageNamesCache || pageNamesCache.sources.some((source, index) => source !== sources[index])) {
    pageNamesCache = {
      sources,
      names: buildPageEntityNames(
        storageNames,
        state.MedicalInsurances?.list,
        state.Staff?.list,
        state.Persons?.list,
        state.TenantPriceLists?.list as Named<{ id: number; listName?: string }>
      ),
    };
  }
  return pageNamesCache.names;
};

//#endregion
